import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import TableContainer from '@mui/material/TableContainer'
import { captureException } from '@sentry/react'
import {
    type InfiniteData,
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query'
import { useSnackbar } from 'notistack'

import { type PaginatedResponse } from '~/api'
import { type Filter, type FilterValue, productApi } from '~/api/product-api'
import useUser from '~/common/hooks/useUser'
import { type Product } from '~/common/schemas/product'
import { useAppSelector } from '~/store'
import { getPath } from '~/utils/getPath'
import { productKeys } from '../products/api/query-keys-factory'
import ProductsTable from '../products/components/products-table'
import ProductsToolbar from '../products/components/products-toolbar'
import { useProductFilter } from '../products/hooks/useProductFilter'

export default function Products() {
    const stores = useAppSelector((app) => app.stores)
    const { enqueueSnackbar } = useSnackbar()
    const { data: filterData, isLoading: filterLoading } = useProductFilter()

    const { filterValues, filterParams, updateFilterValues, toggleExclusiveFilter } =
        useProductFilterValues(filterData)

    const {
        data: productData,
        status: productDataStatus,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useProductListingInfinite(filterParams)

    const products = useMemo(
        () => productData?.pages.flatMap((page) => page.results),
        [productData?.pages]
    )

    const [dialog, setDialog] = useState('')
    function closeDialog() {
        setDialog('')
    }

    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
    const selectedIds = useMemo(() => {
        return products ? Object.keys(rowSelection).map((key) => products?.[parseInt(key)].id) : []
    }, [products, rowSelection])

    const { mutate: updateProductStatus, mutateAsync: updateProductStatusAsync } =
        useUpdateProductStatus(filterParams)

    function handleActiveSwitch(productId: number, status: Product['status']) {
        const product = products?.find((product) => product.id === productId)
        if (product?.status === status) {
            return
        }

        updateProductStatus(
            { id: productId, status },
            {
                onSuccess: () => {
                    enqueueSnackbar(gettext('Product status updated'), { variant: 'success' })
                    window.analytics.track('Change product status', {
                        page: window.location.href,
                        feature: 'product',
                        type: 'update',
                        trigger: 'toggle click',
                        product_id: productId,
                        status_value: status,
                    })
                },
                onError: (error) => {
                    captureException(error)
                    enqueueSnackbar(gettext('Failed to update product status'), {
                        variant: 'error',
                    })
                },
            }
        )
    }

    async function handleBatchStatusUpdate(status: 'active' | 'inactive') {
        if (!selectedIds.length) {
            return
        }

        try {
            await Promise.all(selectedIds.map((id) => updateProductStatusAsync({ id, status })))

            window.analytics.track('Change product status', {
                page: window.location.href,
                feature: 'product',
                type: 'bulk update',
                trigger: 'button click',
                product_ids: selectedIds,
                status_value: status,
            })

            enqueueSnackbar(gettext('Batch product status updated'), { variant: 'success' })
            setRowSelection({})
        } catch (error) {
            captureException(error)
            enqueueSnackbar(gettext('Failed to update some product status'), { variant: 'error' })
        }
    }

    const { mutateAsync: deleteProductAsync, isLoading: isDeletingProduct } =
        useDeleteProduct(filterParams)

    async function handleDeleteProducts() {
        if (!selectedIds.length) {
            return
        }

        try {
            await Promise.all(selectedIds.map((id) => deleteProductAsync(id)))

            window.analytics.track('Delete product', {
                page: window.location.href,
                feature: 'product',
                type: 'bulk delete',
                trigger: 'button click',
                product_ids: selectedIds,
            })

            setRowSelection({})
            setDialog('')
        } catch (error) {
            captureException(error)
            enqueueSnackbar(gettext('Failed to delete some products'), { variant: 'error' })
        }
    }

    function handleFilterChange(field: Filter) {
        return (option: FilterValue | null) => updateFilterValues(field.lookup, option)
    }

    function handleFilterSelectExclusive(field: Filter) {
        return () => toggleExclusiveFilter(field.lookup)
    }

    function handleSearchChange(value: string) {
        updateFilterValues('search', { label: 'search', value })
    }

    return (
        <>
            <Box
                component={Paper}
                sx={(theme) => ({
                    width: '100%',
                    height: '100%',
                    overflowY: 'hidden',
                    position: 'relative',

                    '--products-toolbar-max-height': `${theme.spacing(12)}`,

                    [theme.breakpoints.up('xl')]: {
                        '--products-toolbar-max-height': `${theme.spacing(8)}`,
                    },
                })}
            >
                <ProductsToolbar
                    loading={filterLoading}
                    numSelected={Object.keys(rowSelection).length}
                    filterValues={filterValues}
                    filterOptions={filterData}
                    onMakeActive={() => handleBatchStatusUpdate('active')}
                    onMakeInactive={() => handleBatchStatusUpdate('inactive')}
                    onDelete={() => setDialog('delete')}
                    onNewProductClick={() => setDialog('new-product')}
                    onFilterChange={handleFilterChange}
                    onSelectExclusive={handleFilterSelectExclusive}
                    onSearchChange={handleSearchChange}
                />

                <TableContainer
                    sx={{
                        height: `calc(100% - var(--products-toolbar-max-height))`,
                        overflowY: 'auto',
                    }}
                >
                    <ProductsTable
                        stickyHeader
                        status={productDataStatus}
                        products={products}
                        rowSelection={rowSelection}
                        canFetchMore={hasNextPage}
                        isFetchingMore={isFetchingNextPage}
                        onRowSelectionChange={setRowSelection}
                        onStatusUpdate={handleActiveSwitch}
                        onFetchMore={fetchNextPage}
                    />
                </TableContainer>
            </Box>
            <Dialog
                open={dialog === 'delete'}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                onClose={closeDialog}
            >
                <DialogTitle id="alert-dialog-title">
                    {gettext('Delete these products?')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {gettext('Products will be removed from all linked channels.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={isDeletingProduct} color="primary" onClick={closeDialog}>
                        {gettext('Cancel')}
                    </Button>
                    <Button
                        autoFocus
                        disabled={isDeletingProduct}
                        color="primary"
                        onClick={handleDeleteProducts}
                    >
                        {gettext('Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                fullWidth
                open={dialog === 'new-product'}
                onClose={closeDialog}
                maxWidth="xs"
                aria-labelledby="select-store"
            >
                <DialogTitle id="select-store">{gettext('Select store')}</DialogTitle>
                <div>
                    <List>
                        {stores.map((store) => (
                            <Link
                                key={store.id}
                                component={RouterLink}
                                to={getPath('createProduct', { store_id: store.id.toString() })}
                                sx={{ textDecoration: 'none', color: 'inherit' }}
                            >
                                <ListItemButton>
                                    <ListItemText primary={store.name} />
                                </ListItemButton>
                            </Link>
                        ))}
                    </List>
                </div>
            </Dialog>
        </>
    )
}

function useProductFilterValues(data?: Filter[]) {
    const [filterValues, setFilterValues] = useState(() => {
        return new Map<string, FilterValue | FilterValue[] | null>().set('status', {
            label: 'Active',
            value: 'active',
        })
    })

    useEffect(() => {
        if (!data) {
            return
        }

        setFilterValues(
            data.reduce(
                (acc, { lookup }) => {
                    acc.set(
                        lookup,
                        lookup === 'status' ? { label: 'Active', value: 'active' } : null
                    )

                    return acc
                },
                new Map() as typeof filterValues
            )
        )
    }, [data])

    function toggleExclusiveFilter(lookup: string) {
        const key = `${lookup}__exclusive`
        setFilterValues(
            (prev) =>
                new Map(prev.set(key, prev.get(key) ? null : { label: 'Exclusive', value: 'True' }))
        )
    }

    function updateFilterValues(lookup: string, option: FilterValue | null) {
        setFilterValues((prev) => new Map(prev.set(lookup, option)))
    }

    const filterParams = useMemo(() => {
        return [...filterValues.entries()].reduce(
            (acc, [key, filter]) => {
                if (!filter) {
                    return acc
                }

                if (Array.isArray(filter)) {
                    acc[key] = filter.map(({ value }) => value.toString())
                    return acc
                }

                if (filter.value) {
                    acc[key] = [filter.value.toString()]
                }

                return acc
            },
            {} as Record<string, string[]>
        )
    }, [filterValues])

    return {
        filterValues: Object.fromEntries(filterValues),
        filterParams,
        toggleExclusiveFilter,
        updateFilterValues,
    }
}

function useUpdateProductStatus(params: Record<string, string[]>) {
    const user = useUser()
    const queryClient = useQueryClient()
    const queryKey = productKeys.listing(user.id, params)

    return useMutation({
        mutationFn: ({ id, status }: { id: Product['id']; status: Product['status'] }) => {
            return productApi.updateProductStatus(id, status)
        },
        onMutate: async ({ id, status }) => {
            const previousProducts = queryClient.getQueryData(queryKey)

            await queryClient.cancelQueries(queryKey)

            queryClient.setQueryData<InfiniteData<PaginatedResponse<Product>>>(queryKey, (old) => {
                if (!old) {
                    return
                }

                return {
                    ...old,
                    pages: old.pages.map((page) => ({
                        ...page,
                        results: page.results.map((product) => ({
                            ...product,
                            status: product.id === id ? status : product.status,
                        })),
                    })),
                }
            })

            return { previousProducts }
        },
        onError: (_, __, context) => {
            queryClient.setQueryData(queryKey, context?.previousProducts)
        },
    })
}

function useDeleteProduct(params: Record<string, string[]>) {
    const user = useUser()
    const queryClient = useQueryClient()
    const queryKey = productKeys.listing(user.id, params)

    return useMutation({
        mutationFn: (id: Product['id']) => productApi.deleteProduct(id),
        onSuccess: () => queryClient.invalidateQueries(queryKey),
    })
}

const FETCH_LIMIT = 25

function useProductListingInfinite(params: Record<string, string[]>) {
    const user = useUser()
    return useInfiniteQuery({
        enabled: true,
        queryKey: productKeys.listing(user.id, params),
        queryFn: ({ pageParam = 0 }) => {
            return productApi.getProductListing(user.id, {
                ...params,
                limit: FETCH_LIMIT,
                offset: FETCH_LIMIT * pageParam,
            })
        },
        getNextPageParam: (lastPage) => {
            if (!lastPage.next) {
                return
            }

            const url = new URL(lastPage.next)
            const offset = parseInt(url.searchParams.get('offset') ?? '')
            if (isNaN(offset)) {
                return
            }

            return Math.ceil(offset / FETCH_LIMIT)
        },
    })
}
